import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
export const API = `${BACKEND_URL}/api`;

const client = axios.create({ baseURL: API, timeout: 60000 });
// Per-file upload gets its own longer timeout because a slow mobile
// connection can spend 30-45s pushing a single 8MB passport scan.
// The application submission itself (post-refactor) sends only tiny
// JSON references, so 60s remains fine for that call.
const UPLOAD_TIMEOUT_MS = 180000; // 3 minutes per file

// ---- token helpers ----
const TOKEN_KEY = 'waan_affiliate_token';
export const getToken = () => localStorage.getItem(TOKEN_KEY) || '';
export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

client.interceptors.request.use((cfg) => {
  const t = getToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

// ---- pre-application single-file upload ----
// Upload one file to the server, return {id, upload_token, ...} that can
// be sent later as a file_references[] entry on POST /api/applications.
// Emits onProgress(percent) for the UI progress bar.
export async function uploadApplicationFile({ file, kind, travelerIndex, onProgress, signal }) {
  if (!file) throw new Error('No file provided');
  const fd = new FormData();
  fd.append('kind', kind);
  if (travelerIndex !== undefined && travelerIndex !== null) fd.append('traveler_index', String(travelerIndex));
  fd.append('file', file);
  const res = await client.post('/uploads/pre-application', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: UPLOAD_TIMEOUT_MS,
    signal,
    onUploadProgress: (e) => {
      if (!onProgress) return;
      const pct = e.total ? Math.round((e.loaded / e.total) * 100) : 0;
      onProgress(pct);
    },
  });
  return res.data;
}

// ---- applications ----
// New API: application submission sends only server-issued file
// references (no raw file bytes). Frontend must upload each file first
// via `uploadApplicationFile` and pass the resulting `{id, upload_token,
// kind, traveler_index?}` in `data.file_references`. Total request
// stays a few KB even for 4-applicant groups, eliminating the >50 MB
// monolithic multipart POST that used to time out on slow mobile
// connections.
export async function submitApplication(data) {
  const res = await client.post('/applications', data);
  return res.data;
}

export async function trackApplication(ref) {
  const res = await client.get(`/track/${encodeURIComponent(ref)}`);
  return res.data;
}

export async function listPaymentProviders() {
  const res = await client.get('/payments/providers');
  return res.data;
}

export async function confirmPayment(ref, intentId) {
  const res = await client.post('/payments/confirm', { ref, intent_id: intentId });
  return res.data;
}

export async function retryPaypalOrder(ref) {
  // Create a fresh PayPal order for an application that already went through
  // a failed capture attempt (e.g. PAYER_CANNOT_PAY locked the previous
  // order). The application is NEVER duplicated - only `payment.intent_id`
  // is replaced server-side.
  const res = await client.post(`/payments/paypal/retry/${encodeURIComponent(ref)}`);
  return res.data;
}

// ---- contact ----
export async function submitContact(payload) {
  const res = await client.post('/contacts', payload);
  return res.data;
}

// ---- affiliates ----
export async function registerAffiliate(payload) {
  const res = await client.post('/affiliates/register', payload);
  if (res.data?.token) setToken(res.data.token);
  return res.data;
}
export async function loginAffiliate(payload) {
  const res = await client.post('/affiliates/login', payload);
  if (res.data?.token) setToken(res.data.token);
  return res.data;
}
export async function me() {
  const res = await client.get('/affiliates/me');
  return res.data;
}
export async function forgotPassword(email) {
  const res = await client.post('/affiliates/forgot-password', { email });
  return res.data;
}
export async function resetPassword(token, password) {
  const res = await client.post('/affiliates/reset-password', { token, password });
  return res.data;
}
export async function verifyEmail(token) {
  const res = await client.get('/affiliates/verify-email', { params: { token } });
  return res.data;
}

export default client;
